import {computed, inject, Injectable, signal} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {Apollo} from 'apollo-angular';
import {firstValueFrom} from 'rxjs';

import {GET_POLICIES} from '../queries-repository';
import {JanusService} from './janus.service';
import {Organization, Policy, Query} from '../types-repository';
import {ContextService} from './context.service';
import {isGranted} from './policy-access';

/** The action that gates the monitoring dashboard. Granting it to an org is what makes the page appear. */
export const MONITORING_ACTION = 'contentplatform:status:Query/ProcessStats';

/**
 * Records when the dashboard was last looked at, so a fresh login re-opens it once the numbers are
 * stale. localStorage rather than a cookie, matching WhatsNewService: the value never leaves the
 * browser, so it needs no consent under GDPR/ePrivacy.
 */
const VIEWED_KEY = 'cp-monitoring-viewed';
/** Earlier versions stored the marker in a cookie of the same name; expire it. */
const LEGACY_COOKIE = VIEWED_KEY;

/** How long a look at the dashboard counts for before a login sends the user back to it. */
export const VIEWED_WINDOW_MS = 4 * 60 * 60 * 1000;

/**
 * Finds an organization the signed-in user can open the monitoring dashboard under.
 *
 * The dashboard needs one because the AppSync authorizer resolves a caller's policies **only** from
 * the membership matching the `X-Castlabs-Organization` header, so the page only works in an
 * organization whose role grants `contentplatform:status:Query/*`. `ContextService.isAllowedAnywhere`
 * cannot answer this: despite the name it checks the *current* organization for any *resource*, so the
 * nav entry would come and go as the user switched organization.
 *
 * There is no single API call for "which of my organizations grant this action" -- `Query.policies`
 * answers for the organization in the header and nothing else -- so this asks per organization. To keep
 * that affordable it short-circuits on the current organization first (the common case for someone who
 * is already looking at the platform team org), probes the rest one at a time with early exit, and
 * caches for the session.
 */
@Injectable({providedIn: 'root'})
export class MonitoringAccessService {
  private apollo = inject(Apollo);
  private context = inject(ContextService);
  private janus = inject(JanusService);

  private readonly granting = signal<Organization | null>(null);
  /**
   * The in-flight (or finished) probe.
   *
   * Cached as the promise itself, not as a boolean "already started" flag: several callers await this
   * -- the shell on render and the welcome screen when deciding where to land -- and a flag makes the
   * second caller return immediately while the first is still resolving. That looked exactly like a
   * broken redirect: the nav entry appeared once the probe landed, but the login had already decided
   * there was nothing to open.
   */
  private probe: Promise<void> | null = null;

  constructor() {
    document.cookie = `${LEGACY_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  }

  /** An organization that grants monitoring, or null if none does (or it has not been resolved yet). */
  readonly organization = computed(() => this.granting());

  /**
   * Resolve which organization to link to, once per session.
   *
   * Safe to call repeatedly -- the nav calls it on render.
   */
  resolve(): Promise<void> {
    if (!this.probe) {
      this.probe = this.runProbe();
    }
    return this.probe;
  }

  /** Does the work behind {@link resolve}, exactly once per session. */
  private async runProbe(): Promise<void> {
    // Check the current organization first -- its policies are already loaded, so for a platform-team
    // member browsing their own org this makes the whole fan-out unnecessary.
    //
    // Read straight off the signal rather than through ContextService.isAllowedAnywhere: this runs as
    // the shell initialises, when policies() can still be null, and that method calls .some() on it
    // unguarded. A null here just means "not known yet", which is handled by leaving the current org
    // in the fan-out below rather than trusting a negative.
    const current = this.context.organization();
    const currentPolicies = this.context.policies();
    if (current && currentPolicies) {
      if (MonitoringAccessService.grants(currentPolicies)) {
        this.granting.set(current);
        return;
      }
    }

    let organizations: Organization[];
    try {
      organizations = JanusService.organizationsOf(await firstValueFrom(this.janus.userinfo$));
    } catch {
      // Without the org list there is nothing to probe; leaving the entry hidden is the safe outcome,
      // and the API would refuse the page anyway.
      return;
    }

    // Only skip the current organization if its policies were actually loaded and said no; an
    // unresolved current org still has to be probed.
    const currentWasEvaluated = !!(current && currentPolicies);
    const candidates = organizations
      .filter(org => !(currentWasEvaluated && org.id === current.id))
      // The monitoring action is only granted by the platform team's own role (see
      // policies_cpteam_admin.json), so probe that org first: the members it matters for are exactly
      // the ones with many memberships, and this resolves them on the first probe.
      .sort((a, b) => Number(b.legacy_name === 'castlabs_cpteam') - Number(a.legacy_name === 'castlabs_cpteam'));

    // One probe at a time with early exit, NOT Promise.all: each probe is a full AppSync request and
    // authorizer invocation (the authorizer response is uncacheable, ttlOverride 0), so a parallel
    // fan-out turns every page load into a burst of N requests. Sequential is cheap upstream — the
    // authorizer's shared userinfo cache is already warm from the organizations query above, so each
    // probe costs one authorizer run and no IdP call.
    for (const org of candidates) {
      if (await this.grantsMonitoring(org)) {
        this.granting.set(org);
        return;
      }
    }
    this.granting.set(null);
  }

  /** Remembers that the dashboard was looked at now, so the next few logins land normally. */
  markViewed(): void {
    // Storage can throw (private mode, blocked site data); then the next login just lands on the dashboard again.
    try {
      localStorage.setItem(VIEWED_KEY, new Date().toISOString());
    } catch {
      // ignore
    }
  }

  /**
   * @return Whether the dashboard has been looked at recently enough that a login should not
   *         interrupt with it. False when it has never been looked at, so the page gets discovered.
   */
  viewedRecently(): boolean {
    const viewed = this.lastViewed();
    return viewed !== null && Date.now() - viewed < VIEWED_WINDOW_MS;
  }

  /**
   * @return When the dashboard was last looked at, or null if never / unparseable.
   */
  private lastViewed(): number | null {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(VIEWED_KEY);
    } catch {
      return null;
    }
    if (!stored) {
      return null;
    }
    const viewed = Date.parse(stored);
    return isNaN(viewed) ? null : viewed;
  }

  /**
   * @param policies Policy documents for one organization, possibly null while still loading.
   * @return Whether any statement in them grants the monitoring action.
   */
  private static grants(policies: Policy[] | null | undefined): boolean {
    return isGranted(policies, MONITORING_ACTION, null);
  }

  /**
   * @param org The organization to ask about.
   * @return The organization if its policies grant monitoring, otherwise null.
   */
  private async grantsMonitoring(org: Organization): Promise<Organization | null> {
    try {
      const result = await firstValueFrom(
        this.apollo.query<Query>({
          query: GET_POLICIES(org.id),
          // Asking about an organization other than the active one, so the header is set explicitly.
          // The Apollo auth middleware only fills it in when it is absent.
          context: {headers: new HttpHeaders().set('X-Castlabs-Organization', org.id)},
          // MUST be no-cache. The `policies` field is cached per *active* organization
          // (`headerAware` in app.config.ts keys on ContextService.organizationId(), not on the header
          // sent), so caching a probe for another organization would file its policies under the
          // organization the user is currently looking at -- and ContextService reads that same field
          // to drive every isAllowed() check in the app. 'network-only' is not enough: it skips the
          // read but still writes.
          fetchPolicy: 'no-cache',
        })
      );
      return MonitoringAccessService.grants(result.data?.policies?.policies) ? org : null;
    } catch {
      // An organization the user cannot read policies for simply does not grant monitoring.
      return null;
    }
  }
}
